import React from 'react';
import { Badge, Card, CardBody, Col, Row, Table } from 'react-bootstrap';
import IFormStep from 'interfaces/IFormStep';
import HelpButton from '../../../Form/HelpButton';
import _ from 'lodash';
import IActivity from '../../../../interfaces/IActivity';
import ActivityModal from './ActivityModal';
import ActivityActions from './ActivityActions';
import GaugeChart from 'react-gauge-chart';

const Activities: React.FC<IFormStep> = ({ formData, requirements, handleInputChange, models }) => {
    const help = requirements.config?.help && requirements.config?.help['activities'];
    const activities = formData.activities ?? [];

    const numberOfActivitiesRequired = 90;

    const sortActivities = (activities: IActivity[]) => {
        return _.sortBy(activities, (a) => {
            return a['date'];
        });
    };

    const generateNewActivityTemplate = (template?: IActivity): IActivity => {
        if (template) {
            return {
                provider: template.provider,
            };
        }

        if (!activities?.length) {
            return {};
        }

        const { provider } = activities[activities.length - 1];

        return {
            provider,
        };
    };

    const [editableActivity, setEditableActivity] = React.useState<IActivity>(
        generateNewActivityTemplate()
    );
    const [keepModalOpen, setKeepModalOpen] = React.useState(false);

    const handleEditableActivityUpdate = (name: string, value: any) => {
        setEditableActivity((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleActivitiesChange = (value: IActivity[]) => {
        handleInputChange('activities', sortActivities(value));
    };

    const handleUpdateActivity = (index: number) => {
        const updatedActivities = activities.map((cc, i) => {
            if (i === index) {
                return editableActivity;
            }

            return cc;
        });
        handleActivitiesChange(updatedActivities);
    };

    const handleAddNewActivity = () => {
        handleActivitiesChange(activities.concat([editableActivity]));
        setEditableActivity(generateNewActivityTemplate(editableActivity));
    };

    const handleDeleteActivity = (index: number) => {
        handleActivitiesChange(_.filter(activities, (_item, i) => i !== index));
        setEditableActivity(generateNewActivityTemplate());
    };

    const isValid = (activity: IActivity) => {
        return models.formValidator.validateActivity(activity);
    };

    const minimumSpecificEmdrCredits = 50;
    const minimumTotalCreditsHours = 75;

    const specificEmdrCredits = activities.reduce(
        (total, activity) => total + (activity?.specificEmdrCredits ?? 0),
        0
    );
    const relatedCreditsHours = activities.reduce(
        (total, activity) => total + (activity?.relatedCreditsHours ?? 0),
        0
    );
    const totalCreditsHours =
        specificEmdrCredits +
        Math.min(relatedCreditsHours, minimumTotalCreditsHours - specificEmdrCredits);

    return (
        <>
            <Row>
                <h3 className="fw-bold mb-3">
                    EMDR activity {help && <HelpButton title={'Activities'} content={help} />}
                </h3>
            </Row>
            <Row className={'mb-3 col-10 text-black fw-normal'}>
                <h4 className={'fw-normal'}>
                    Add details for each activity separately. A new record entry form will appear
                    automatically when you complete a record.
                </h4>
                <p>
                    A total of 75 credits/hours is required with at least 50 specific EMDR credits.
                </p>
            </Row>
            <Row>
                <Col xs={12}>
                    <Card className="mb-3 shadow rounded" border="light">
                        <CardBody>
                            <Row>
                                <Col xs={4}>
                                    <h5 className="fw-bold">Specific EMDR Credits</h5>
                                    <GaugeChart
                                        id="specific-emdr-credits-gauge"
                                        animate={false}
                                        // colors={['#FFC371', '#356e23']}
                                        colors={['#856404', '#c4e76a']}
                                        arcWidth={0.3}
                                        percent={specificEmdrCredits / minimumTotalCreditsHours}
                                        arcPadding={0.1}
                                        cornerRadius={3}
                                        arcsLength={[0.66, 0.33]}
                                        formatTextValue={(value) =>
                                            `${specificEmdrCredits} / ${minimumSpecificEmdrCredits}`
                                        }
                                        textColor={'#23356f'}
                                    />
                                </Col>
                                <Col xs={4}>
                                    <h5 className="fw-bold">Total Credits/Hours</h5>
                                    <GaugeChart
                                        id="total-credits-hours-gauge"
                                        animate={false}
                                        nrOfLevels={1}
                                        colors={['#23356f']}
                                        arcWidth={0.3}
                                        percent={totalCreditsHours / minimumTotalCreditsHours}
                                        arcPadding={0.1}
                                        cornerRadius={3}
                                        textColor={'#23356f'}
                                        formatTextValue={(value) =>
                                            `${totalCreditsHours} / ${minimumTotalCreditsHours}`
                                        }
                                    />
                                </Col>
                                <Col xs={4}>
                                    {totalCreditsHours >= minimumTotalCreditsHours &&
                                    specificEmdrCredits >= minimumSpecificEmdrCredits ? (
                                        <Badge bg="primary" className="mb-2">
                                            100% Complete
                                        </Badge>
                                    ) : (
                                        <Badge className="mb-2 bg-warning rounded-pill">
                                            {Math.round(
                                                (totalCreditsHours / minimumTotalCreditsHours) * 100
                                            )}
                                            % complete
                                        </Badge>
                                    )}
                                    <dl>
                                        <dt>Specific EMDR Credits</dt>
                                        <dd>
                                            {specificEmdrCredits}/{minimumSpecificEmdrCredits} (
                                            {minimumSpecificEmdrCredits - specificEmdrCredits}{' '}
                                            credits remaining)
                                        </dd>
                                        <dt>Related Credits/Hours</dt>
                                        <dd>{relatedCreditsHours}</dd>
                                        <dt>Total Credits/Hours</dt>
                                        <dd>
                                            {totalCreditsHours}/{minimumTotalCreditsHours} (
                                            {minimumTotalCreditsHours - totalCreditsHours}{' '}
                                            credits/hours remaining)
                                        </dd>
                                    </dl>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {activities.length > 0 && (
                <Row className={'mb-4'}>
                    <Col xs={12}>
                        <Card className="mb-3" border="light">
                            <Table
                                responsive
                                striped
                                className={'activity-table align-middle table-sm'}
                            >
                                <thead>
                                    <tr>
                                        <th className={'text-center'}>#</th>
                                        <th>Date</th>
                                        <th>Event & Title</th>
                                        <th>Provider</th>
                                        <th>Speaker(s)</th>
                                        <th>Specific EMDR Credits</th>
                                        <th>Related Credits/Hours</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white">
                                    {activities.map((activity, index) => (
                                        <tr key={'activity-' + index}>
                                            <td className={'text-center'}>{index + 1}</td>
                                            <td className={'text-black'}>
                                                {new Date(activity.date!).toLocaleDateString()}
                                            </td>
                                            <td className={'text-black'}>
                                                {activity.eventAndTitle}
                                            </td>
                                            <td className={'text-black'}>{activity.provider}</td>
                                            <td className={'text-black'}>{activity.speakers}</td>
                                            <td className={'text-black'}>
                                                {activity.specificEmdrCredits || '-'}
                                            </td>
                                            <td className={'text-black'}>
                                                {activity.relatedCreditsHours || '-'}
                                            </td>
                                            <td>
                                                <ActivityActions
                                                    index={index}
                                                    activity={activity}
                                                    editableActivity={editableActivity}
                                                    handleUpdateActivity={handleUpdateActivity}
                                                    handleDeleteActivity={handleDeleteActivity}
                                                    handleEditableActivityUpdate={
                                                        handleEditableActivityUpdate
                                                    }
                                                    generateNewActivityTemplate={
                                                        generateNewActivityTemplate
                                                    }
                                                    setEditableActivity={setEditableActivity}
                                                    isValid={isValid}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className={'border-bottom border-dark'}>
                                    <tr>
                                        <td colSpan={4}></td>
                                        <td className={'text-black fw-bold'}>Totals:</td>
                                        <td className={'text-black fw-bold'}>
                                            {activities.reduce(
                                                (total, activity) =>
                                                    total + (activity?.specificEmdrCredits ?? 0),
                                                0
                                            )}
                                        </td>
                                        <td className={'text-black fw-bold'}>
                                            {activities.reduce(
                                                (total, activity) =>
                                                    total + (activity?.relatedCreditsHours ?? 0),
                                                0
                                            )}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </Card>
                    </Col>
                </Row>
            )}
            {activities?.length < numberOfActivitiesRequired && (
                <Row>
                    <Col xs={5}>
                        <ActivityModal
                            editableActivity={editableActivity}
                            handleEditableActivityUpdate={handleEditableActivityUpdate}
                            handleAddNewActivity={handleAddNewActivity}
                            setEditableActivity={setEditableActivity}
                            isValid={isValid}
                            generateNewActivityTemplate={generateNewActivityTemplate}
                            keepModalOpen={keepModalOpen}
                            setKeepModalOpen={setKeepModalOpen}
                        />
                    </Col>
                </Row>
            )}
        </>
    );
};

export default Activities;
